import {
  Paper,
  IconButton,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Checkbox,
} from "@mui/material";
import React, { useEffect, useId, useState } from "react";
import { useDispatch } from "react-redux";
import { taxSlice } from "../../../store/reducer/tax";
import { MaskTypes } from "../../../utils/models";
import FileUploadIcon from "../../../assets/file_upload_icon.svg";
import EditIcon from "@mui/icons-material/Edit";
import { toDollar, toFloatString } from "../../../utils/dataConverter";
import { TaxDocument } from "../../../model/tax/tax_document.d";
import {
  emptyW2Form,
  IRSW2Form,
  LocalWithholding,
  validateLocalWithholding,
} from "../../../model/tax/irsw2.d";
import MaskTextInput from "../../../components/MaskTextInput";
import TextInput from "../../../components/TextInput";
import { ErrorAlert } from "../../../components/Alerts";
import { ValidatorForm } from "react-material-ui-form-validator";
import GreenCircleCheckbox from "../../../components/GreenCircleCheckbox";

interface ConfirmW2Props {
  deleteFile: (index: number) => void;
  index: number;
  w2: TaxDocument<IRSW2Form>;
  editMode: boolean | undefined;
  hasSpouse: boolean;
  userName: string | undefined;
  spouseName: string | undefined;
  toggleEditMode: (value: boolean) => void;
  wagesMatch: boolean;
}
const ConfirmW2 = ({
  deleteFile,
  index,
  w2,
  editMode,
  hasSpouse,
  userName,
  spouseName,
  toggleEditMode,
}: ConfirmW2Props) => {
  const dispatch = useDispatch();

  const showFieldErrors = true;
  const getShowLwhError = (lwh: LocalWithholding) => {
    console.log('validateLocalWithholding', lwh, validateLocalWithholding(lwh));
    return showFieldErrors && !validateLocalWithholding(lwh);
  };

  // missing box 18 is only a warning, not an error
  const getShowLwhWarning = (lwh: LocalWithholding) => {
    return showFieldErrors && validateLocalWithholding(lwh) && !lwh.local_wages_and_tips;
  }

  const addLocalTaxesField = () => {
    dispatch(taxSlice.actions.addLocalTax(index));
  };

  const deleteLocalTaxesField = (idx: number) => {
    dispatch(taxSlice.actions.deleteLocalTax({ w2Idx: index, transIdx: idx }));
  };

  useEffect(() => {
    if (editMode === undefined) {
      toggleEditMode(
        Object.values({ ...w2.form_data, user_id: 1 }).some(
          (value) => value === ""
        )
      );
    } else if (editMode) {
      if (w2.form_data.employer_address === null) {
        dispatch(
          taxSlice.actions.updateW2ValueChange({
            key: "employer_address",
            value: emptyW2Form.employer_address,
            index: index,
          })
        );
      } else if (w2.form_data.employer_tax_id == null) {
        dispatch(
          taxSlice.actions.updateW2ValueChange({
            key: "employer_tax_id",
            value: emptyW2Form.employer_tax_id,
            index: index,
          })
        );
      } else if (w2.form_data.is_third_party_sick_pay == null) {
        dispatch(
          taxSlice.actions.updateW2ValueChange({
            key: "is_third_party_sick_pay",
            value: emptyW2Form.is_third_party_sick_pay,
            index: index,
          })
        );
      }
    }
  }, [editMode, toggleEditMode]);

  const w2OwnerLabelId = `w2-owner-label${useId()}`;

  return (
    <div className={"file-info"} id={w2.resource_uri}>
      <Paper elevation={0} className={"file-wrapper"}>
        {(w2.warnings ?? {}).uploaded_document?.map((warning: string, index: number) => (
          <ErrorAlert key={`w2-alert-${index}`}>{warning}</ErrorAlert>
        ))}
        <div className={"uploaded-file-container"}>
          <p className={"file-name-wrapper"}>
            <img
              src={FileUploadIcon}
              alt={"File upload icon"}
              className={"file-img"}
            />
            {w2.uploaded_file.file_name}
          </p>
          <div className={"review-link"}>
            {!editMode ? (
              <IconButton
                className={"edit-btn"}
                onClick={() => toggleEditMode(true)}
                size="large"
              >
                <EditIcon className={"edit-icon"} />
              </IconButton>
            ) : (
              <button className={"delete-link"} onClick={() => deleteFile(index)}>
                delete
              </button>
            )}
          </div>
        </div>
        <div className="uploaded-file-container">
          <h3>W-2 Individual Tax Statement</h3>
          <button className={"delete-link"} onClick={() => deleteFile(index)}>
            delete
          </button>
        </div>
        {editMode ? (
          <ValidatorForm onSubmit={() => { }}>
            <Grid className={"form-row"} size={12}>
              {hasSpouse && (
                <FormControl>
                  <RadioGroup
                    aria-labelledby={w2OwnerLabelId}
                    row
                    className="form-horizontal"
                    value={w2.form_data.document_owner}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      dispatch(
                        taxSlice.actions.updateW2ValueChange({
                          key: "document_owner",
                          value: e.target.value,
                          index: index,
                        })
                      )
                    }
                  >
                    <h4 id={w2OwnerLabelId} style={{ margin: 0 }}>This W-2 is for:</h4>
                    <div>
                      <FormControlLabel
                        value="self"
                        control={<Radio />}
                        label={userName ? userName : "Me"}
                      />
                      <FormControlLabel
                        value="spouse"
                        control={<Radio />}
                        label={spouseName ? spouseName : "My Spouse"}
                      />
                      {showFieldErrors && !w2.form_data.document_owner && (
                        <span style={{ color: "red" }}>
                          Please select an option
                        </span>
                      )}
                    </div>
                  </RadioGroup>
                </FormControl>
              )}

              <TextInput
                aria-required
                name="employer_name"
                label="Employer Name"
                placeholder="Employer Name"
                className="form-row"
                value={w2.form_data.employer_name}
                helperText={
                  showFieldErrors && !w2.form_data.employer_name
                    ? "Must not be empty"
                    : ""
                }
                error={showFieldErrors && !w2.form_data.employer_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch(
                    taxSlice.actions.updateW2ValueChange({
                      key: "employer_name",
                      value: e.target.value,
                      index: index,
                    })
                  )
                }
              />
              <MaskTextInput
                variant="outlined"
                name="employer_tax_id"
                aria-required
                label="Employer Federal Tax ID"
                placeholder="Employer Federal Tax ID"
                className="form-row"
                value={w2.form_data.employer_tax_id}
                helperText={
                  !w2.form_data.employer_tax_id
                    ? "Must not be empty"
                    : String(w2.form_data.employer_tax_id).trim().length !== 10
                      ? "Must be 9 digits"
                      : null
                }
                error={
                  showFieldErrors &&
                  (!w2.form_data.employer_tax_id ||
                    String(w2.form_data.employer_tax_id).trim().length !== 10)
                }
                maskType={MaskTypes.EmployerTaxId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch(
                    taxSlice.actions.updateW2ValueChange({
                      key: "employer_tax_id",
                      value: e.target.value,
                      index: index,
                    })
                  )
                }
              />
              <TextInput
                aria-required
                name="employer_address_address_1"
                label="Employer Address Line 1"
                placeholder="Employer Address Line 1"
                className="form-row"
                value={w2.form_data.employer_address?.address_1}
                helperText={
                  showFieldErrors && !w2.form_data.employer_address?.address_1
                    ? "Must not be empty"
                    : null
                }
                error={
                  showFieldErrors && !w2.form_data.employer_address?.address_1
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch(
                    taxSlice.actions.updateW2ValueChange({
                      key: "employer_address.address_1",
                      value: e.target.value,
                      index: index,
                    })
                  )
                }
              />
              <TextInput
                name="employer_address_address_2"
                label="Employer Address Line 2"
                placeholder="Employer Address Line 2"
                className="form-row"
                value={w2.form_data.employer_address?.address_2}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch(
                    taxSlice.actions.updateW2ValueChange({
                      key: "employer_address.address_2",
                      value: e.target.value,
                      index: index,
                    })
                  )
                }
              />
              <TextInput
                aria-required
                name="employer_address_city"
                label="Employer City"
                placeholder="Employer City"
                className="text-field form-row"
                value={w2.form_data.employer_address?.city}
                helperText={
                  showFieldErrors && !w2.form_data.employer_address?.city
                    ? "Must not be empty"
                    : ""
                }
                error={showFieldErrors && !w2.form_data.employer_address?.city}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch(
                    taxSlice.actions.updateW2ValueChange({
                      key: "employer_address.city",
                      value: e.target.value,
                      index: index,
                    })
                  )
                }
              />
              <TextInput
                aria-required
                name="employer_address_state"
                label="Employer State"
                placeholder="Employer State"
                className="form-row"
                value={w2.form_data.employer_address?.state}
                helperText={
                  showFieldErrors && !w2.form_data.employer_address?.state
                    ? "Must not be empty"
                    : ""
                }
                error={showFieldErrors && !w2.form_data.employer_address?.state}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch(
                    taxSlice.actions.updateW2ValueChange({
                      key: "employer_address.state",
                      value: e.target.value,
                      index: index,
                    })
                  )
                }
              />
              <TextInput
                aria-required
                name="employer_address_zip_code"
                label="Employer Zip"
                placeholder="Employer Zip"
                className="form-row"
                value={w2.form_data.employer_address?.zip_code}
                helperText={
                  showFieldErrors && !w2.form_data.employer_address?.zip_code
                    ? "Must not be empty"
                    : ""
                }
                error={
                  showFieldErrors && !w2.form_data.employer_address?.zip_code
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  dispatch(
                    taxSlice.actions.updateW2ValueChange({
                      key: "employer_address.zip_code",
                      value: e.target.value,
                      index: index,
                    })
                  )
                }
              />
            </Grid>
            <Grid container spacing={2}>
              <Grid
                className={"form-row"}
                size={{
                  xs: 6,
                  md: 6
                }}>
                <MaskTextInput
                  variant="outlined"
                  name="wages_tips_other_comp_box1"
                  ariaRequired
                  label="1 Wages, tips, other comp."
                  placeholder=""
                  maskType={MaskTypes.Dollar}
                  value={w2.form_data.wages_tips_other_comp_box1}
                  helperText={showFieldErrors && !w2.form_data.wages_tips_other_comp_box1 && "Must not be empty"}
                  error={
                    showFieldErrors && !w2.form_data.wages_tips_other_comp_box1
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    dispatch(
                      taxSlice.actions.updateW2ValueChange({
                        key: "wages_tips_other_comp_box1",
                        value: toFloatString(e.target.value),
                        index: index,
                      })
                    )
                  }
                />
              </Grid>
              <Grid
                className={"form-row"}
                size={{
                  xs: 6,
                  md: 6
                }}>
                <MaskTextInput
                  variant="outlined"
                  name="medicare_wages_and_tips"
                  ariaRequired
                  label="5 Medicare wages and tips"
                  placeholder=""
                  maskType={MaskTypes.Dollar}
                  value={w2.form_data.medicare_wages_and_tips}
                  helperText={
                    showFieldErrors && !w2.form_data.medicare_wages_and_tips &&
                    <span>
                      If there is a dollar amount reported in Box 5 of your W-2,
                      enter it here. If there is no dollar amount in Box 5
                      please{" "}
                      <i>
                        <u>
                          <a href="mailto:limehelp@cityofspringboro.com">
                            contact us
                          </a>
                        </u>
                      </i>{" "}
                      for assistance.
                    </span>
                  }
                  error={
                    showFieldErrors && !w2.form_data.medicare_wages_and_tips
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (!w2.form_data.medicare_wages_and_tips && e.target.value === "") {
                      return;
                    }
                    dispatch(
                      taxSlice.actions.updateW2ValueChange({
                        key: "medicare_wages_and_tips",
                        value: toFloatString(e.target.value),
                        index: index,
                      })
                    )
                  }}
                />
              </Grid>
              {/*<Grid xs={6} md={6} className={"form-row"}>
                <FormControlLabel
                  name="is_third_party_sick_pay"
                  label="13 Third-party sick pay"
                  className='label-root'
                  control={
                    <Checkbox
                      aria-required
                      className='chkbox'
                      value={w2.form_data.is_third_party_sick_pay}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        dispatch(
                          taxSlice.actions.updateW2ValueChange({
                            key: "is_third_party_sick_pay",
                            value: e.target.checked,
                            index: index,
                          })
                        )
                      }
                    />
                  }
                >
                </FormControlLabel>
              </Grid>*/}
            </Grid>

            {(w2.form_data.local_withholdings ?? []).map(
              (withholding: LocalWithholding, idx: number) => (
                <Grid container spacing={2} key={`${index}_lwh${idx}`}>
                  <Grid size={12}>
                    <div className={"uploaded-file-container"}>
                      <h4 className={"form-edit-section-title"}>
                        Local Withholding {idx + 1}
                      </h4>

                      <button
                        className={"delete-link form-edit-section-title"}
                        onClick={() => deleteLocalTaxesField(idx)}
                      >
                        delete
                      </button>
                    </div>
                  </Grid>
                  <Grid
                    size={{
                      xs: 6,
                      md: 4
                    }}>
                    <MaskTextInput
                      variant="outlined"
                      name="local_wages_and_tips"
                      ariaRequired
                      label={
                        <FormLabel
                          color={getShowLwhWarning(withholding) ? "warning" : undefined}
                        >
                          18 Local wages, tips, etc.
                        </FormLabel>
                      }
                      placeholder=""
                      maskType={MaskTypes.Dollar}
                      value={withholding.local_wages_and_tips}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        dispatch(
                          taxSlice.actions.updateW2ValueChange({
                            key: "local_wages_and_tips",
                            value: toFloatString(e.target.value),
                            index: index,
                            transIdx: idx,
                          })
                        );
                      }}
                      helperText={
                        getShowLwhWarning(withholding) &&
                        "Review your W-2 to ensure this field is correct."
                      }
                    />
                  </Grid>

                  <Grid
                    size={{
                      xs: 6,
                      md: 4
                    }}>
                    <MaskTextInput
                      variant="outlined"
                      name="local_income_tax"
                      ariaRequired
                      label="19 Local Income Tax"
                      placeholder=""
                      maskType={MaskTypes.Dollar}
                      value={withholding.local_income_tax}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        dispatch(
                          taxSlice.actions.updateW2ValueChange({
                            key: "local_income_tax",
                            value: toFloatString(e.target.value),
                            index: index,
                            transIdx: idx,
                          })
                        )
                      }
                      helperText={
                        getShowLwhError(withholding) &&
                        (!withholding.local_income_tax) &&
                        "Must not be empty. Enter 0 if your W-2 does not have a number here."
                      }
                      error={
                        getShowLwhError(withholding) &&
                        (!withholding.local_income_tax)
                      }
                    />
                  </Grid>
                  <Grid
                    className={"form-row"}
                    size={{
                      xs: 12,
                      md: 4
                    }}>
                    <TextInput
                      variant="outlined"
                      aria-required
                      name="locality_name"
                      label="20 Locality name"
                      placeholder=""
                      value={withholding.locality_name}
                      // NOTE backend needs objects for individual withholdings
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        dispatch(
                          taxSlice.actions.updateW2ValueChange({
                            key: "locality_name",
                            value: e.target.value,
                            index: index,
                            transIdx: idx,
                          })
                        )
                      }
                      error={
                        getShowLwhError(withholding) &&
                        !withholding.locality_name
                        && !Number(toFloatString(withholding.locality_name))
                        && !!Number(toFloatString(withholding.local_income_tax))
                      }
                      helperText={
                        getShowLwhError(withholding) &&
                        (!Number(toFloatString(withholding.locality_name)) && !!Number(toFloatString(withholding.local_income_tax))) && [
                          "You must enter the name of the city / local entity reported in Box 20 of your W-2",
                        ]
                      }
                    />
                  </Grid>
                </Grid>
              )
            )}
            <button
              onClick={addLocalTaxesField}
              className={"add-local-tax-btn"}
            >
              + Add Local Withholding
            </button>
          </ValidatorForm>
        ) : (
          <>
            <div className={"form-static"}>
              <label className={"form-static-title"}>
                Employer’s Name, Address, and Zip Code
              </label>
              <label key={"employer_name"} className={"form-static-value"}>
                {w2.form_data.employer_name}
              </label>
              <label key={"employer_address"} className={"form-static-value"}>
                {w2.form_data.employer_address?.address_1}
              </label>
              <label key={"employer_address_2"} className={"form-static-value"}>
                {w2.form_data.employer_address?.address_2}
              </label>
              <label key={"employer_city"} className={"form-static-value"}>
                {w2.form_data.employer_address?.city}
              </label>
              <label key={"employer_state"} className={"form-static-value"}>
                {w2.form_data.employer_address?.state}
              </label>
              <label key={"employer_zip"} className={"form-static-value"}>
                {w2.form_data.employer_address?.zip_code}
              </label>
            </div>
            <div className={"form-static"}>
              <label className={"form-static-title"}>
                Employer’s Federal Tax ID
              </label>
              <label key={"employer_tax_id"} className={"form-static-value"}>
                {w2.form_data.employer_tax_id}
              </label>
            </div>
            <div className={"form-static"}>
              <label className={"form-static-title"}>
                1 Wages, tips, other comp.
              </label>
              <label className={"form-static-value"}>
                {toDollar(
                  (w2 as TaxDocument<IRSW2Form>).form_data
                    .wages_tips_other_comp_box1
                )}
              </label>
            </div>
            <div className={"form-static"}>
              <label className={"form-static-title"}>
                5 Medicare wages and tips
              </label>
              <label className={"form-static-value"}>
                {toDollar(
                  (w2 as TaxDocument<IRSW2Form>).form_data
                    .medicare_wages_and_tips
                )}
              </label>
            </div>
            {(
              (w2 as TaxDocument<IRSW2Form>).form_data.local_withholdings ?? []
            ).map((withholding: LocalWithholding, idx: number) => (
              <Grid container key={`${index}_${idx}lwh`}>
                <Grid
                  size={{
                    xs: 12,
                    md: 12
                  }}>
                  <label className={"form-static-section"}>
                    Local Withholding {idx + 1}
                  </label>
                </Grid>

                <Grid
                  className={"form-static-compressed"}
                  size={{
                    xs: 6,
                    md: 4
                  }}>
                  <label className={"form-static-title"}>
                    18 Local wages, tips, etc.
                  </label>
                  <label className={"form-static-value"}>
                    {toDollar(withholding.local_wages_and_tips)}
                  </label>
                </Grid>
                <Grid
                  className={"form-static-compressed"}
                  size={{
                    xs: 6,
                    md: 4
                  }}>
                  <label className={"form-static-title"}>
                    19 Local Income Tax
                  </label>
                  <label className={"form-static-value"}>
                    {toDollar(withholding.local_income_tax)}
                  </label>
                </Grid>
                <Grid
                  className={"form-static"}
                  size={{
                    xs: 12,
                    md: 4
                  }}>
                  <label className={"form-static-title"}>
                    20 Locality name
                  </label>
                  <label className={"form-static-value"}>
                    {withholding.locality_name}
                  </label>
                </Grid>
              </Grid>
            ))}
          </>
        )}
      </Paper>
    </div>
  );
};

export default ConfirmW2;
